import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'

import { createQueryKeys, queryClient, type ApiError } from '../core'
import type { CreateTemplateVersionResponseDto, GenerateVariationBody, TemplateVersionFilters } from './template-versions.schemas'
import { templateVersionsService } from './template-versions.service'

export const templateVersionsKeys = createQueryKeys('template-versions')

export const templateVersionsQueries = {
  ofProject: (projectId: string, filters: Omit<TemplateVersionFilters, 'projectId'> = {}) =>
    queryOptions({ queryKey: templateVersionsKeys.all({ projectId, ...filters }), queryFn: ({ signal }) => templateVersionsService.ofProject(projectId, filters, signal) }),
}

/** `useTemplateVersions(projectId)`: the project's template variations, newest first. Asked for once and kept. Pass `undefined` to hold off. */
export function useTemplateVersions(projectId: string | undefined, filters: Omit<TemplateVersionFilters, 'projectId'> = {}) {
  return useQuery({ ...templateVersionsQueries.ofProject(projectId ?? '', filters), enabled: Boolean(projectId), staleTime: Infinity })
}

/** Asks the server again, now: called when the task that draws a new version has finished, because that is when its pictures exist. */
export function refreshTemplateVersions(projectId: string) {
  return queryClient.fetchQuery({ ...templateVersionsQueries.ofProject(projectId), staleTime: 0 })
}

/**
 * `useGenerateVariation().mutateAsync({ projectId, ...body })`: starts one variation and answers with it and the task to follow. Several can be
 * started together (Generate all): each call is its own mutation result, so none waits for another. Never retried by itself.
 */
export function useGenerateVariation() {
  return useMutation<CreateTemplateVersionResponseDto, ApiError, GenerateVariationBody & { projectId: string }>({
    mutationFn: ({ projectId, ...body }) => templateVersionsService.generate(projectId, body),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: templateVersionsKeys.all({ projectId }) }),
  })
}
